import {
  AfterContentChecked,
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  ElementRef,
  Input,
  OnDestroy,
  QueryList,
  ViewChild,
  forwardRef
} from '@angular/core';
import { Observable, Subject, Subscription, fromEvent, merge } from 'rxjs';
import { auditTime, takeUntil } from 'rxjs/operators';
import { TabNavBarLinkDirective } from './tab-nav-bar-link.directive';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'tab-nav-bar',
  templateUrl: './tab-nav-bar.component.html',
  styleUrls: ['./tab-nav-bar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TabNavBarComponent implements AfterContentInit, AfterContentChecked, OnDestroy {
  @ContentChildren(forwardRef(() => TabNavBarLinkDirective), { descendants: true })
  public items!: QueryList<TabNavBarLinkDirective>;
  @ViewChild('tabItemsContainer', { static: true })
  public tabItemsContainer!: ElementRef;
  @ViewChild('tabItems', { static: true })
  public tabList!: ElementRef;
  @ViewChild('tabNavBar', { static: true })
  public tabNavBar!: ElementRef;

  @Input()
  get selectedIndex(): number | null {
    return this.lastSelectedIndex;
  }
  set selectedIndex(value: number | null) {
    if (this.lastSelectedIndex !== value) {
      this.selectedIndexChanged = true;
      this.lastSelectedIndex = value;
    }
  }

  private scrollDistance: number = 0;
  private scrollDistanceHasChanged!: boolean;
  public disableScrollBefore: boolean = true;
  public disableScrollAfter: boolean = true;
  public showPaginationControls: boolean = false;
  private lastSelectedIndex: number | null = null;
  private selectedIndexChanged: boolean = false;
  private tabCount!: number;

  private viewPortChange: Observable<Event>;
  private viewportSize!: { width: number; height: number };
  private viewPortSubscription: Subscription;

  private readonly onDestroy: Subject<void> = new Subject();

  constructor(private readonly cd: ChangeDetectorRef) {
    this.viewPortChange = merge(fromEvent(window, 'resize'), fromEvent(window, 'orientationchange'));
    this.viewPortSubscription = this.viewPortChange.subscribe(() => this.updateViewportSize());
  }

  ngOnDestroy() {
    this.viewPortSubscription.unsubscribe();
  }

  ngAfterContentInit() {
    const resize = this.viewPortChange.pipe(auditTime(150));
    merge(resize, this.items.changes)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.updatePagination();
      });
  }

  ngAfterContentChecked() {
    if (this.tabCount !== this.items.length) {
      this.updatePagination();
      this.tabCount = this.items.length;
      this.cd.markForCheck();
    }

    if (this.selectedIndexChanged) {
      this.scrollToThisTab(this.selectedIndex || 0);
      this.checkScrollingControls();
      this.updateTabScrollPosition();
      this.selectedIndexChanged = false;
      this.cd.markForCheck();
    }

    if (this.scrollDistanceHasChanged) {
      this.updateTabScrollPosition();
      this.scrollDistanceHasChanged = false;
      this.cd.markForCheck();
    }
  }

  private updatePagination(): void {
    this.checkIfPaginationButtonIsToBeShown();
    this.checkScrollingControls();
    this.updateTabScrollPosition();
  }

  private checkIfPaginationButtonIsToBeShown(): void {
    const isEnabled = this.tabList.nativeElement.scrollWidth > this.tabNavBar.nativeElement.offsetWidth;

    if (!isEnabled) {
      this.scrollDistance = 0;
    }

    this.showPaginationControls = isEnabled;
  }

  private updateTabScrollPosition(): void {
    const scrollDistance = this.scrollDistance;
    const translateX = this.getLayoutDirection() === 'ltr' ? -scrollDistance : scrollDistance;
    this.tabList.nativeElement.style.transform = `translateX(${Math.round(translateX)}px)`;
  }

  public handlePagination(direction: string): void {
    if ((direction === 'before' && this.disableScrollBefore) || (direction === 'after' && this.disableScrollAfter)) {
      return;
    }
    const viewLength = this.tabItemsContainer.nativeElement.offsetWidth;
    const scrollAmount = ((direction === 'before' ? -1 : 1) * viewLength) / 3;
    this.scrollTo(this.scrollDistance + scrollAmount);
  }

  private scrollTo(position: number) {
    const maxScrollDistance = this.getMaxScrollDistance();
    this.scrollDistance = Math.max(0, Math.min(maxScrollDistance, position));
    this.scrollDistanceHasChanged = true;
    this.checkScrollingControls();
    return { maxScrollDistance, distance: this.scrollDistance };
  }

  private scrollToThisTab(selectedIndex: number): void {
    const selectedTab = this.items ? this.items.toArray()[selectedIndex] : null;

    if (!selectedTab) {
      return;
    }

    const viewLength = this.tabItemsContainer.nativeElement.offsetWidth;
    const { offsetLeft, offsetWidth } = selectedTab.elementRef.nativeElement;

    let labelBeforePos: number;
    let labelAfterPos: number;
    if (this.getLayoutDirection() === 'ltr') {
      labelBeforePos = offsetLeft;
      labelAfterPos = labelBeforePos + offsetWidth;
    } else {
      labelAfterPos = this.tabList.nativeElement.offsetWidth - offsetLeft;
      labelBeforePos = labelAfterPos - offsetWidth;
    }

    const beforeVisiblePos = this.scrollDistance;
    const afterVisiblePos = this.scrollDistance + viewLength;

    if (labelBeforePos < beforeVisiblePos) {
      this.scrollDistance -= beforeVisiblePos - labelBeforePos;
    } else if (labelAfterPos > afterVisiblePos) {
      this.scrollDistance += labelAfterPos - afterVisiblePos;
    }
  }

  private checkScrollingControls(): void {
    this.disableScrollBefore = this.scrollDistance === 0;
    this.disableScrollAfter = this.scrollDistance === this.getMaxScrollDistance();
    this.cd.markForCheck();
  }

  private getMaxScrollDistance(): number {
    const lengthOfTabList = this.tabList.nativeElement.scrollWidth;
    const viewLength = this.tabItemsContainer.nativeElement.offsetWidth;
    return lengthOfTabList - viewLength || 0;
  }

  private updateViewportSize() {
    this.viewportSize = { width: window.innerWidth, height: window.innerHeight };
  }

  private getLayoutDirection() {
    return this.direction && this.direction === 'rtl' ? 'rtl' : 'ltr';
  }

  get direction() {
    const bodyDir = document.body ? document.body.dir : null;
    const htmlDir = document.documentElement ? document.documentElement.dir : null;
    const value = bodyDir || htmlDir;
    return value === 'ltr' || value === 'rtl' ? value : 'ltr';
  }
}
